import { useMemo, useState, useEffect } from "react"
import { useMap } from "react-map-gl/mapbox"
import { area, featureCollection, union } from "@turf/turf"
import { Trash2, LockOpen, Check, Plus } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Slider } from "@/components/ui/slider"
import { Input } from "@/components/ui/input"
import type { UseConstructionStudyReturn } from "@/hooks/explore/use-construction-study"
import { RETRAIT_POLYGON_COLOR } from "@/hooks/explore/use-construction-study"
import { formatNumber } from "@/lib/explore/utils/format-helpers"

interface StudyPanelProps {
  study: UseConstructionStudyReturn
}

export const StudyPanel = ({ study }: StudyPanelProps) => {
  const { current: map } = useMap()
  const {
    defaultHeight,
    setDefaultHeight,
    edge,
    setEdge,
    hasRetraitPolygon,
    isRetraitValidated,
    setIsRetraitValidated,
    basePolygon,
    finalPolygons,
    setFinalPolygons,
    fetchedPLUData,
    isLoadingPLUData,
    pluDataNotFound,
    createRetraitPolygon,
    removeRetraitPolygon,
    resetStudy,
    parcelSurface,
  } = study

  // Rules
  const [maxSurfaceAuthorized, setMaxSurfaceAuthorized] = useState<number>((fetchedPLUData?.maxSurface ?? 0) / 100)
  const [maxHeightAuthorized, setMaxHeightAuthorized] = useState<number>(fetchedPLUData?.maxHeight ?? 0)

  useEffect(() => {
    if (fetchedPLUData?.maxHeight) setMaxHeightAuthorized(fetchedPLUData.maxHeight)
    if (fetchedPLUData?.maxSurface) setMaxSurfaceAuthorized(fetchedPLUData.maxSurface / 100)
  }, [fetchedPLUData])

  const calculatedArea = useMemo((): number => {
    const allPolygons = [...finalPolygons]
    if (basePolygon) allPolygons.push(basePolygon)
    if (allPolygons.length === 0) return 0
    if (allPolygons.length === 1) return Math.round(area(allPolygons[0]))
    const merged = union(featureCollection(allPolygons))
    return merged ? Math.round(area(merged)) : 0
  }, [basePolygon, finalPolygons])

  const calculatedSDP = useMemo((): number => {
    let total = 0
    if (basePolygon) {
      total += area(basePolygon) * Math.round((basePolygon.properties?.height ?? 0) / 3) * 0.8
    }
    total += finalPolygons.reduce(
      (acc, poly) => acc + area(poly) * Math.round((poly.properties?.height ?? 0) / 3) * 0.8,
      0,
    )
    return Math.round(total)
  }, [basePolygon, finalPolygons])

  const getBuildingsMaxHeight = useMemo(() => {
    const max = basePolygon?.properties?.height ?? 0
    return finalPolygons.reduce((acc, poly) => Math.max(acc, poly.properties?.height ?? 0), max)
  }, [finalPolygons, basePolygon?.properties?.height])

  const totalSurface = parcelSurface
  const emprisePercent = useMemo(
    () => (totalSurface ? Math.round((calculatedArea / totalSurface) * 100) : null),
    [calculatedArea, totalSurface],
  )
  const empriseOk = useMemo(
    () => (totalSurface ? calculatedArea / totalSurface <= maxSurfaceAuthorized : null),
    [calculatedArea, totalSurface, maxSurfaceAuthorized],
  )

  const heightOk = getBuildingsMaxHeight <= maxHeightAuthorized

  return (
    <div className="absolute top-20 right-2 z-50">
      <div className="bg-white/95 backdrop-blur-sm rounded-xl map-panel-shadow flex flex-col w-80 max-h-[60vh] overflow-y-auto">
        {/* Header */}
        <div className="px-4 pt-3 pb-2 flex items-center justify-between">
          <p className="text-xs font-semibold tracking-wide uppercase text-primary">Etude de faisabilite</p>
          <Button
            variant="ghost"
            size="sm"
            onClick={resetStudy}
            className="text-red-600 hover:bg-red-50 text-[0.7rem] font-medium h-auto py-0.5 px-1.5"
          >
            <Trash2 className="w-3.5 h-3.5 mr-1" />
            Tout effacer
          </Button>
        </div>

        <div className="h-px bg-gray-100 mx-3" />

        {/* PLU Rules */}
        <div className="px-4 py-3">
          <div className="flex items-center gap-2 mb-2">
            <p className="text-xs text-gray-400 font-medium tracking-wide uppercase">Regles PLU</p>
            {isLoadingPLUData && <span className="text-[10px] text-gray-400">Chargement...</span>}
            {pluDataNotFound && !isLoadingPLUData && <span className="text-[10px] text-amber-500">Non trouve</span>}
          </div>
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="block text-[10px] text-gray-400 mb-0.5">Hauteur max (m)</label>
              <Input
                type="number"
                value={maxHeightAuthorized}
                onChange={(e) => setMaxHeightAuthorized(parseInt(e.target.value))}
                className="h-9"
              />
            </div>
            <div className="flex-1">
              <label className="block text-[10px] text-gray-400 mb-0.5">Emprise max (%)</label>
              <Input
                type="number"
                value={maxSurfaceAuthorized * 100}
                onChange={(e) => setMaxSurfaceAuthorized(parseInt(e.target.value) / 100)}
                className="h-9"
              />
            </div>
          </div>
        </div>

        <div className="h-px bg-gray-100 mx-3" />

        {/* Conformite */}
        <div className="px-4 py-3 flex flex-col gap-1.5">
          <p className="text-xs text-gray-400 font-medium tracking-wide uppercase mb-0.5">Conformite</p>
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">Hauteur max</span>
            <span className={`text-[11px] font-medium ${heightOk ? "text-green-600" : "text-red-600"}`}>
              {formatNumber(getBuildingsMaxHeight)}m / {formatNumber(maxHeightAuthorized)}m
            </span>
          </div>
          {emprisePercent != null && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-500">Emprise</span>
              <span className={`text-[11px] font-medium ${empriseOk ? "text-green-600" : "text-red-600"}`}>
                {formatNumber(emprisePercent)}% / {formatNumber(Math.round(maxSurfaceAuthorized * 100))}%
              </span>
            </div>
          )}
        </div>

        <div className="h-px bg-gray-100 mx-3" />

        {/* Surfaces */}
        <div className="px-4 py-3">
          <p className="text-xs text-gray-400 font-medium tracking-wide uppercase mb-2">Surfaces</p>
          <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
            {totalSurface && (
              <>
                <span className="text-xs text-gray-500">Parcelle</span>
                <span className="text-xs font-medium text-gray-800 text-right">
                  {formatNumber(totalSurface)} m{"\u00B2"}
                </span>
              </>
            )}
            <span className="text-xs text-gray-500">Emprise au sol</span>
            <span className="text-xs font-medium text-gray-800 text-right">
              {formatNumber(calculatedArea)} m{"\u00B2"}
            </span>
            <span className="text-xs text-gray-500">SDP</span>
            <span className="text-xs font-medium text-gray-800 text-right">
              {formatNumber(Math.round(calculatedSDP))} m{"\u00B2"}
            </span>
            <span className="text-xs text-gray-500">SHAB</span>
            <span className="text-xs font-medium text-gray-800 text-right">
              {formatNumber(Math.round(calculatedSDP * 0.93))} m{"\u00B2"}
            </span>
            <span className="text-xs text-gray-500">Parking</span>
            <span className="text-xs font-medium text-gray-800 text-right">
              {formatNumber(Math.floor(calculatedArea / 25))} places/niv.
            </span>
            <span className="text-xs text-gray-500">Etages</span>
            <span className="text-xs font-medium text-gray-800 text-right">
              R + {Math.round(getBuildingsMaxHeight / 3) - 1}
            </span>
          </div>
        </div>

        {/* Polygon list */}
        <div className="h-px bg-gray-100 mx-3" />
        <div className="px-4 py-3 flex flex-col gap-1.5">
          {/* Retrait polygon entry */}
          {hasRetraitPolygon && basePolygon && (
            <div
              className="rounded-md px-2 py-1.5 bg-black/[0.02] hover:bg-black/[0.04] transition-colors"
              style={{ borderLeft: `3px solid ${RETRAIT_POLYGON_COLOR}` }}
            >
              <div className="flex items-center gap-1.5">
                <div className="flex flex-col mr-1 min-w-[44px]">
                  <span className="text-xs font-medium text-gray-700">{basePolygon.properties?.height}m</span>
                  <span className="text-[10px] text-gray-400">
                    {formatNumber(Math.round(area(basePolygon)))} m{"\u00B2"}
                  </span>
                </div>
                <span className="text-[10px] text-gray-400 flex-1">Retrait</span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => setIsRetraitValidated(!isRetraitValidated)}
                >
                  {isRetraitValidated ? (
                    <LockOpen className="w-4 h-4 text-green-600" />
                  ) : (
                    <Check className="w-4 h-4 text-gray-400" />
                  )}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-red-500 hover:text-red-600"
                  onClick={removeRetraitPolygon}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
              <div className="mt-1">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-gray-400">Hauteur</span>
                  <span
                    className={`text-[10px] font-medium ${defaultHeight <= maxHeightAuthorized ? "text-green-600" : "text-red-600"}`}
                  >
                    {formatNumber(defaultHeight)}m
                  </span>
                </div>
                <Slider
                  value={[defaultHeight]}
                  onValueChange={(v) => setDefaultHeight(v[0])}
                  disabled={isRetraitValidated}
                  min={0}
                  max={50}
                  step={1}
                  className="my-1"
                />
              </div>
              <div className="-mt-1">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-gray-400">Retrait</span>
                  <span className={`text-[10px] font-medium ${empriseOk ? "text-green-600" : "text-red-600"}`}>
                    {formatNumber(edge)}m
                  </span>
                </div>
                <Slider
                  value={[edge]}
                  onValueChange={(v) => setEdge(v[0])}
                  disabled={isRetraitValidated}
                  min={0}
                  max={20}
                  step={0.5}
                  className="my-1"
                />
              </div>
            </div>
          )}
          {!hasRetraitPolygon && (
            <Button
              onClick={createRetraitPolygon}
              className="font-semibold"
              style={{ backgroundColor: RETRAIT_POLYGON_COLOR }}
            >
              <Plus className="w-3.5 h-3.5 mr-1" />
              Creer emprise retrait
            </Button>
          )}
          {/* Drawn polygons */}
          {finalPolygons.map((poly, i) => {
            const polyValidated = !!poly.properties?.validated
            return (
              <div
                onMouseEnter={() => {
                  map?.setFeatureState(
                    {
                      source: "custom-polygons-source",
                      id: poly.properties?.uniqid,
                    },
                    { hover: true },
                  )
                }}
                onMouseLeave={() => {
                  map?.setFeatureState(
                    {
                      source: "custom-polygons-source",
                      id: poly.properties?.uniqid,
                    },
                    { hover: false },
                  )
                }}
                key={i}
                className="flex items-center gap-1.5 rounded-md px-2 py-1 bg-black/[0.02] hover:bg-black/[0.04] transition-colors"
                style={{ borderLeft: `3px solid ${poly.properties?.color}` }}
              >
                <div className="flex flex-col mr-1 min-w-[44px]">
                  <span className="text-xs font-medium text-gray-700">{poly.properties?.height}m</span>
                  <span className="text-[10px] text-gray-400">
                    {formatNumber(Math.round(area(poly)))} m{"\u00B2"}
                  </span>
                </div>
                <Slider
                  min={0}
                  max={50}
                  step={1}
                  value={[poly.properties?.height ?? 0]}
                  disabled={polyValidated}
                  onValueChange={(newValue) => {
                    const updatedPoly = { ...poly }
                    updatedPoly.properties = {
                      ...updatedPoly.properties,
                      height: newValue[0],
                    }
                    setFinalPolygons([...finalPolygons.slice(0, i), updatedPoly, ...finalPolygons.slice(i + 1)])
                  }}
                  className="flex-1 min-w-[50px]"
                />
                <div className="flex ml-auto">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => {
                      const updatedPoly = { ...poly }
                      updatedPoly.properties = {
                        ...updatedPoly.properties,
                        validated: !polyValidated,
                      }
                      setFinalPolygons([...finalPolygons.slice(0, i), updatedPoly, ...finalPolygons.slice(i + 1)])
                    }}
                  >
                    {polyValidated ? (
                      <LockOpen className="w-4 h-4 text-green-600" />
                    ) : (
                      <Check className="w-4 h-4 text-gray-400" />
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-red-500 hover:text-red-600"
                    onClick={() => {
                      setFinalPolygons([...finalPolygons.slice(0, i), ...finalPolygons.slice(i + 1)])
                    }}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )
          })}
        </div>

        {/* Bottom padding */}
        <div className="h-1" />
      </div>
    </div>
  )
}
